/**
 * @fileoverview V2 Client Information Step — Contact details, demographics, address.
 *
 * Self-contained: owns form state, API hydration, save logic.
 * Patches the primary client on submit. Not audit-tracked.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGetMatterDetailsQuery,
  usePatchMatterClientMutation,
} from '@/services/api/mattersService';
import {
  Gender,
  type GenderValue,
  type MatterClient,
  PREFERRED_LANGUAGE_OPTIONS,
  type PreferredLanguageValue,
} from '@/services/types/matter-client-types';
import { getApiErrorMessage } from '@/utils/api-error';
import { isPhoneNumberValid, sanitizePhoneNumber } from '@/utils/validation';

import {
  FormError,
  FormField,
  FormGroup,
  FormInput,
  FormLabel,
  FormSection,
} from '../components/form';
import { useStepHydration } from '../hooks/useStepHydration';
import { useIngestion } from '../state/IngestionProvider';

// ── Schema ───────────────────────────────────────────────────────────────────

const phoneNumberSchema = z.object({
  id: z.string().optional(),
  phone_number: z.string().refine(
    (value) => {
      if (!value || value.trim() === '') {
        return true;
      }

      return isPhoneNumberValid(value);
    },
    { message: 'Invalid phone number (e.g. 323-334-5768)' },
  ),
});

const clientInfoSchema = z.object({
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  date_of_birth: z.string().optional(),
  gender: z
    .enum([Gender.MALE, Gender.FEMALE, Gender.OTHER])
    .optional()
    .nullable()
    .or(z.literal('')),
  ssn: z.string().optional(),
  last_four_of_ssn: z.string().max(4).optional(),
  preferred_language: z
    .enum(PREFERRED_LANGUAGE_OPTIONS.map((option) => option.value) as [string, ...string[]])
    .optional()
    .nullable()
    .or(z.literal('')),
  phone_numbers: z.array(phoneNumberSchema).optional(),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postcode: z.string().optional(),
    })
    .optional(),
});

type ClientInfoFormValues = z.infer<typeof clientInfoSchema>;

// ── Helpers ──────────────────────────────────────────────────────────────────

function extractLast4FromSSN(ssn: string | undefined | null): string | null {
  if (!ssn) {
    return null;
  }

  const digits = ssn.replace(/\D/g, '');
  return digits.length === 9 ? digits.slice(-4) : null;
}

function isCompleteSSN(ssn: string | undefined | null): boolean {
  if (!ssn) {
    return false;
  }

  return ssn.replace(/\D/g, '').length === 9;
}

function createDefaultValues(): ClientInfoFormValues {
  return {
    email: '',
    date_of_birth: '',
    gender: undefined,
    ssn: '',
    last_four_of_ssn: '',
    preferred_language: undefined,
    phone_numbers: [{ phone_number: '' }],
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postcode: '',
    },
  };
}

function normalizePhoneNumbers(
  value: unknown,
): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => (entry as { phone_number?: string }).phone_number)
    .filter((phoneNumber): phoneNumber is string => Boolean(phoneNumber))
    .sort();
}

function createClientPatch(data: ClientInfoFormValues): Partial<MatterClient> {
  const phoneNumbersToSave = data.phone_numbers
    ?.filter((phone) => phone.phone_number?.trim())
    .map((phone) => ({
      ...(phone.id ? { id: phone.id } : {}),
      phone_number: phone.phone_number.startsWith('+')
        ? phone.phone_number
        : sanitizePhoneNumber(phone.phone_number),
    }));

  const dateOfBirth = /^\d{4}-\d{2}-\d{2}$/.test(data.date_of_birth || '')
    ? data.date_of_birth
    : null;
  const ssnDigits = data.ssn?.replace(/-/g, '') || '';
  const ssnValue = ssnDigits.length === 9 ? data.ssn : null;

  let lastFourSsn: string | null = null;
  if (ssnValue) {
    lastFourSsn = extractLast4FromSSN(ssnValue);
  } else if (data.last_four_of_ssn?.length === 4) {
    lastFourSsn = data.last_four_of_ssn;
  }

  const patch: Partial<MatterClient> = {
    email: data.email || undefined,
    date_of_birth: dateOfBirth || undefined,
    gender: (data.gender as GenderValue) || undefined,
    ssn: ssnValue || undefined,
    last_four_of_ssn: lastFourSsn || undefined,
    preferred_language: (data.preferred_language as PreferredLanguageValue) || undefined,
    address: data.address,
  };

  if (phoneNumbersToSave?.length) {
    (patch as Record<string, unknown>).phone_numbers = phoneNumbersToSave;
  }

  return patch;
}

// ── Component ────────────────────────────────────────────────────────────────

export const ClientInformationStep = (): React.JSX.Element => {
  const { state, dispatch, registerSave, importData } = useIngestion();
  const { matterId } = state;

  const { data: matter } = useGetMatterDetailsQuery(matterId ?? skipToken);
  const [patchClient] = usePatchMatterClientMutation();

  const saved = useMemo(() => {
    const client = matter?.clients?.[0];
    if (!client) {
      return null;
    }

    return {
      email: client.email || '',
      date_of_birth: client.date_of_birth || '',
      gender: client.gender ?? undefined,
      ssn: client.ssn || '',
      last_four_of_ssn: client.last_four_of_ssn || extractLast4FromSSN(client.ssn) || '',
      preferred_language: client.preferred_language ?? undefined,
      phone_numbers:
        client.phone_numbers?.length > 0 ? client.phone_numbers : [{ phone_number: '' }],
      address: {
        line1: client.address?.line1 || '',
        line2: client.address?.line2 || '',
        city: client.address?.city || '',
        state: client.address?.state || '',
        postcode: client.address?.postcode || '',
      },
    };
  }, [matter]);

  const imported = useMemo(() => {
    if (!importData?.payload) {
      return null;
    }

    const clientInfo = importData.payload.client_info;

    return {
      email: clientInfo.email,
      date_of_birth: clientInfo.date_of_birth,
      ssn: clientInfo.ssn,
      last_four_of_ssn: clientInfo.ssn_last_four,
      gender: clientInfo.gender,
      preferred_language: clientInfo.preferred_language,
      phone_numbers:
        clientInfo.phone_number.presence === 'present' && clientInfo.phone_number.value
          ? {
              presence: 'present' as const,
              value: [{ phone_number: String(clientInfo.phone_number.value) }],
            }
          : clientInfo.phone_number,
      address: clientInfo.address,
    };
  }, [importData]);

  const { form, isAssisted } = useStepHydration<ClientInfoFormValues>({
    schema: clientInfoSchema,
    defaults: createDefaultValues(),
    saved,
    imported,
    comparators: {
      phone_numbers: (importedValue, currentValue) => {
        const importedPhoneNumbers = normalizePhoneNumbers(importedValue);
        const currentPhoneNumbers = normalizePhoneNumbers(currentValue);

        if (importedPhoneNumbers.length === 0) {
          return false;
        }

        return (
          JSON.stringify(importedPhoneNumbers) === JSON.stringify(currentPhoneNumbers)
        );
      },
    },
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const phoneNumbers = watch('phone_numbers') || [];
  const watchedSSN = useWatch({ control, name: 'ssn' });
  const ssnIsComplete = isCompleteSSN(watchedSSN);

  useEffect(() => {
    if (!ssnIsComplete) {
      return;
    }

    const derivedLastFour = extractLast4FromSSN(watchedSSN);
    if (derivedLastFour) {
      setValue('last_four_of_ssn', derivedLastFour, { shouldValidate: true });
    }
  }, [watchedSSN, ssnIsComplete, setValue]);

  const saveForm = useCallback(
    async (data: ClientInfoFormValues): Promise<boolean> => {
      if (!matterId) {
        toast.warning('Matter not found. Skipping.');
        return true;
      }

      const primaryClient = matter?.clients?.[0];
      if (!primaryClient) {
        toast.warning('No client found. Skipping.');
        return true;
      }

      try {
        await patchClient({
          matter_id: matterId,
          matter_client_id: primaryClient.id,
          patch: createClientPatch(data),
        }).unwrap();

        toast.success('Client information saved');
        return true;
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to save client information'));
        return false;
      }
    },
    [matter, matterId, patchClient],
  );

  useEffect(() => {
    registerSave(async () => {
      let didSave = false;

      await handleSubmit(
        async (data) => {
          didSave = await saveForm(data);
        },
        () => {
          didSave = false;
        },
      )();

      return didSave;
    });

    return () => registerSave(null);
  }, [registerSave, handleSubmit, saveForm]);

  async function onSubmit(data: ClientInfoFormValues): Promise<void> {
    const didSave = await saveForm(data);
    if (didSave) {
      dispatch({ type: 'GO_TO_STEP', index: state.stepIndex + 1 });
    }
  }

  function addPhone(): void {
    setValue('phone_numbers', [...phoneNumbers, { phone_number: '' }]);
  }

  function removePhone(index: number): void {
    setValue(
      'phone_numbers',
      phoneNumbers.filter((_, phoneIndex) => phoneIndex !== index),
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <FormSection title="Contact">
        <FormGroup>
          <FormField hasError={Boolean(errors.email)} assisted={isAssisted('email')}>
            <FormLabel>Email</FormLabel>
            <FormInput
              type="email"
              placeholder="jane.doe@example.com"
              invalid={Boolean(errors.email)}
              disabled={isSubmitting}
              aria-label="Email"
              {...register('email')}
            />
            <FormError>{errors.email?.message}</FormError>
          </FormField>

          <FormField assisted={isAssisted('phone_numbers')}>
            <FormLabel>Phone Numbers</FormLabel>
            <div className="space-y-2" data-slot="control">
              {phoneNumbers.map((_, index) => (
                <div key={index} className="flex gap-2">
                  <FormInput
                    type="tel"
                    placeholder="555-123-4567"
                    invalid={Boolean(errors.phone_numbers?.[index]?.phone_number)}
                    disabled={isSubmitting}
                    aria-label={`Phone number ${index + 1}`}
                    {...register(`phone_numbers.${index}.phone_number`)}
                  />
                  {phoneNumbers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePhone(index)}
                      className="shrink-0 rounded-lg border border-zinc-200 px-2 py-1 text-xs text-zinc-500 hover:border-zinc-300 hover:text-zinc-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addPhone}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
              >
                + Add phone number
              </button>
            </div>
          </FormField>
        </FormGroup>
      </FormSection>

      <FormSection title="Demographics">
        <FormGroup>
          <div className="grid grid-cols-2 gap-4">
            <FormField assisted={isAssisted('date_of_birth')}>
              <FormLabel>Date of Birth</FormLabel>
              <FormInput
                type="date"
                disabled={isSubmitting}
                aria-label="Date of Birth"
                {...register('date_of_birth')}
              />
            </FormField>

            <FormField assisted={isAssisted('gender')}>
              <FormLabel>Gender</FormLabel>
              <Controller
                name="gender"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={(value as string) || undefined}
                    onValueChange={onChange}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger aria-label="Gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField assisted={isAssisted('ssn')}>
              <FormLabel>Social Security Number</FormLabel>
              <FormInput
                type="text"
                inputMode="numeric"
                maxLength={11}
                placeholder="123-45-6789"
                disabled={isSubmitting}
                aria-label="Social Security Number"
                {...register('ssn')}
              />
            </FormField>

            <FormField assisted={isAssisted('last_four_of_ssn')}>
              <FormLabel>Last 4 of SSN</FormLabel>
              <FormInput
                type="text"
                inputMode="numeric"
                maxLength={4}
                placeholder="6789"
                disabled={isSubmitting || ssnIsComplete}
                aria-label="Last 4 of SSN"
                {...register('last_four_of_ssn')}
              />
              {ssnIsComplete && (
                <p className="mt-0.5 text-[10px] text-zinc-400">Auto-filled from SSN</p>
              )}
            </FormField>
          </div>

          <FormField assisted={isAssisted('preferred_language')}>
            <FormLabel>Preferred Language</FormLabel>
            <Controller
              name="preferred_language"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={(value as string) || undefined}
                  onValueChange={onChange}
                  disabled={isSubmitting}
                >
                  <SelectTrigger aria-label="Preferred language">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {PREFERRED_LANGUAGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormField>
        </FormGroup>
      </FormSection>

      <FormSection title="Address">
        <FormGroup>
          <FormField assisted={isAssisted('address')}>
            <FormLabel>Street Address</FormLabel>
            <FormInput
              placeholder="123 Main St"
              disabled={isSubmitting}
              aria-label="Street Address"
              {...register('address.line1')}
            />
          </FormField>

          <FormField assisted={isAssisted('address')}>
            <FormLabel>Address Line 2</FormLabel>
            <FormInput
              placeholder="Apt 4B"
              disabled={isSubmitting}
              aria-label="Address Line 2"
              {...register('address.line2')}
            />
          </FormField>

          <div className="grid grid-cols-3 gap-4">
            <FormField assisted={isAssisted('address')}>
              <FormLabel>City</FormLabel>
              <FormInput
                placeholder="Los Angeles"
                disabled={isSubmitting}
                aria-label="City"
                {...register('address.city')}
              />
            </FormField>

            <FormField assisted={isAssisted('address')}>
              <FormLabel>State</FormLabel>
              <FormInput
                placeholder="CA"
                maxLength={2}
                disabled={isSubmitting}
                aria-label="State"
                {...register('address.state')}
              />
            </FormField>

            <FormField assisted={isAssisted('address')}>
              <FormLabel>Zip Code</FormLabel>
              <FormInput
                placeholder="90012"
                disabled={isSubmitting}
                aria-label="Zip Code"
                {...register('address.postcode')}
              />
            </FormField>
          </div>
        </FormGroup>
      </FormSection>

      <button
        type="submit"
        disabled={isSubmitting}
        className={
          'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ' +
          'bg-blue-600 text-white shadow-sm hover:bg-blue-700 ' +
          'disabled:cursor-not-allowed disabled:opacity-50 ' +
          'transition-colors duration-150'
        }
      >
        {isSubmitting ? 'Saving…' : 'Save & Continue'}
      </button>
    </form>
  );
}
